import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { i18n } from '../i18n/i18n';
import { ApiError, listEvents, createEventApi, deleteEventApi, updateEventApi, uploadEventImageApi, removeEventImageApi, resolveAssetUrl, addEventReminder, clearEventReminders } from '../services/apiService';
import { useAppContext } from '../context/AppContext';
import { EventDto } from 'shared/types/dto';
import { getSupabaseForApi } from '../services/supabaseClient';
import { KidIcon } from './kid/KidIcon';
import AntMascot from './AntMascot';
import { KidButton, KidCard, KidSectionHeading, PictureTile } from './kid/KidUI';
import { StoredPicture } from './kid/StoredPicture';
import { useConfirm } from './kid/useConfirm';
import { useSheet } from '../hooks/useSheet';
import { avatarOrDefault } from '../utils/avatars';
import { starsText } from './kid/KidTaskCard';
import { useLanguage } from '../context/LanguageContext';
import { dayKey, dayRelation, daysFor, defaultNewEventTimes, isoToLocalInput, localInputToIso, overdueTasks, periodContains, plannedTaskEntries, rangeFor, shiftPeriod, tasksByDueDay, taskDueInfo } from '../utils/calendarNav';

const VIEWS = ['day', 'week', 'month'] as const;

type ViewType = typeof VIEWS[number];

const CalendarPage: React.FC = () => {
  const { current_family, current_user } = useAppContext() as any;
  const { current_language } = useLanguage();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [view, setView] = useState<ViewType>('day');
  const [memberFilter, setMemberFilter] = useState<string | 'all'>('all');
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [reminderOffset, setReminderOffset] = useState<number>(0);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState<boolean>(false);
  const [showEventModal, setShowEventModal] = useState<boolean>(false);
  const [familyViewEnabled, setFamilyViewEnabled] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [viewingId, setViewingId] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const getMemberAvatarSrc = (member: any): string => {
    const direct = member?.avatar_url || member?.avatar || member?.profile_image || member?.profile_image_url;
    return resolveAssetUrl(avatarOrDefault(direct));
  };

  // Date helpers
  const getLocale = (): string => {
    try {
      return (i18n as any).language || navigator.language || 'en-US';
    } catch {
      return 'en-US';
    }
  };

  const startOfDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const formatDayHeader = (d: Date): string => {
    return d.toLocaleDateString(getLocale(), { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const toDateKey = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getISOWeekNumber = (d: Date): number => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    // Thursday in current week decides the year.
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date as any) - (yearStart as any)) / 86400000 + 1) / 7);
  };

  const groupEventsByStartDay = (list: EventDto[]): Record<string, EventDto[]> => {
    return list.reduce((acc, ev) => {
      const d = new Date(ev.start_at);
      const key = toDateKey(startOfDayLocal(d));
      if (!acc[key]) acc[key] = [];
      acc[key].push(ev);
      return acc;
    }, {} as Record<string, EventDto[]>);
  };

  const fetchRange = useMemo(() => {
    const { from, to } = rangeFor(view, selectedDate || new Date());
    return { from: from.toISOString(), to: to.toISOString() };
  }, [view, selectedDate]);

  const columnHeaderTitle = useMemo(() => {
    const base = selectedDate || new Date();
    if (view === 'day') {
      return formatDayHeader(base);
    }
    if (view === 'week') {
      const weekNo = getISOWeekNumber(base);
      return `${i18n.t('calendar.week_label') || i18n.t('calendar.view_week')} ${weekNo}`;
    }
    // month
    return base.toLocaleDateString(getLocale(), { month: 'long', year: 'numeric' });
  }, [view, selectedDate]);

  // ‹ › move one day / week / month depending on the view; "Today" jumps back.
  const shiftCurrentPeriod = (delta: number) => {
    setSelectedDate(prev => shiftPeriod(prev || new Date(), view, delta));
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const shouldFilterByMember = !familyViewEnabled && memberFilter !== 'all';
      const data = await listEvents({ ...fetchRange, member_id: shouldFilterByMember ? (memberFilter as string) : undefined });
      setEvents(data);
    } catch (e: any) {
      setError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_family?.id, fetchRange.from, fetchRange.to, memberFilter, familyViewEnabled]);

  // Real-time updates via Supabase Realtime
  useEffect(() => {
    if (!current_family?.id) return;
    // The signed-in user's own client: PIN members now have a real session, so Realtime works
    // for them too (it used to run on the parked guardian session, or none).
    const rt = getSupabaseForApi();
    const channel = rt
      .channel(`events-family-${current_family.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
          filter: `family_id=eq.${current_family.id}`,
        },
        () => fetchEvents(),
      )
      .subscribe();
    return () => {
      rt.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_family?.id, fetchRange.from, fetchRange.to, memberFilter, familyViewEnabled]);

  useSheet(!!viewingId && !showEventModal, () => setViewingId(null));
  useSheet(showEventModal, () => { setShowEventModal(false); resetForm(); });

  // Children may only change events they created (event-mutate + RLS enforce this). Hide the
  // buttons for other events, and translate the server's refusal if it happens anyway.
  const canModifyEvent = (ev: EventDto): boolean =>
    !(current_user?.member_type === 'child' && !current_user?.is_admin) || ev.created_by_user_id === current_user?.id;

  const friendlyError = (err: any): string => {
    if (err instanceof ApiError && err.code === 'not_event_owner') return i18n.t('calendar.not_allowed_edit_event');
    return err?.message || i18n.t('calendar.error_generic');
  };

  const resetForm = () => {
    setTitle(''); setStartAt(''); setEndAt(''); setAllDay(false); setEditingId(null);
    setSelectedAttendees([]);
    setReminderOffset(0);
    setImageFile(null);
    setRemoveImage(false);
  };

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) return;
    try {
      setLoading(true);
      setError(null);
      const isAdmin = !!current_user?.is_admin;
      const attendeesParam = (current_user?.member_type === 'child' && !current_user?.is_admin)
        ? [current_user.id]
        : (isAdmin ? selectedAttendees : undefined);
      if (editingId) {
        const updated = await updateEventApi(editingId, { title, start_at: localInputToIso(startAt), end_at: localInputToIso(endAt), all_day: allDay, attendees: attendeesParam });
        if (removeImage) {
          await removeEventImageApi(updated.id);
        } else if (imageFile) {
          await uploadEventImageApi(updated.id, imageFile);
        }
        // Replace the reminder only when it changed (adding used to stack a second one, and an
        // untouched reminder keeps its "already sent" mark).
        const before = events.find((ev) => ev.id === editingId)?.reminders || [];
        const unchanged = before.length === 1 && before[0].offset_minutes === reminderOffset;
        if (!unchanged && !(before.length === 0 && reminderOffset === 0)) {
          await clearEventReminders(updated.id);
          if (reminderOffset > 0) await addEventReminder(updated.id, reminderOffset);
        }
      } else {
        const created = await createEventApi({ title, start_at: localInputToIso(startAt), end_at: localInputToIso(endAt), all_day: allDay, attendees: attendeesParam });
        if (imageFile) {
          await uploadEventImageApi(created.id, imageFile);
        }
        if (reminderOffset > 0) await addEventReminder(created.id, reminderOffset);
      }
      resetForm();
      setShowEventModal(false);
      await fetchEvents();
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ev: EventDto) => {
    if (!canModifyEvent(ev)) { setError(i18n.t('calendar.not_allowed_edit_event')); return; }
    setEditingId(ev.id);
    setTitle(ev.title);
    setStartAt(isoToLocalInput(ev.start_at));
    setEndAt(isoToLocalInput(ev.end_at));
    setAllDay(!!ev.all_day);
    setImageFile(null);
    setRemoveImage(false);
    if (current_user?.is_admin) {
      setSelectedAttendees(ev.attendees || []);
    }
    setReminderOffset(ev.reminders?.[0]?.offset_minutes ?? 0);
    setViewingId(null);
    setShowEventModal(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: i18n.t('calendar.delete_confirm_message'),
      confirmLabel: i18n.t('calendar.event_form_delete'),
      cancelLabel: i18n.t('calendar.event_form_cancel'),
    });
    if (!ok) return;
    try {
      await deleteEventApi(id);
      setViewingId(null);
      await fetchEvents();
    } catch (err: any) {
      setError(friendlyError(err));
    }
  };

  const isChild = current_user?.member_type === 'child' && !current_user?.is_admin;
  const isAdmin = !!current_user?.is_admin;
  const members = (current_family?.members as any[]) || [];

  // ── items per day: events + tasks due that day ──
  const locale = current_language || getLocale();
  const eventsByDay = groupEventsByStartDay(events);
  const taskOwner = isChild ? current_user?.id : (memberFilter !== 'all' ? (memberFilter as string) : null);
  const allTasks = (current_family?.tasks || []) as any[];
  // Real tasks plus faint "planned" repeats of tasks on a fixed plan (every Thursday …, 2026-10-14).
  const withPlanned = (owner: string | null | undefined) => {
    const real = tasksByDueDay(allTasks, owner);
    const planned = plannedTaskEntries(allTasks, owner);
    const out: Record<string, any[]> = { ...real };
    for (const [k, list] of Object.entries(planned)) out[k] = [...(out[k] || []), ...list];
    return out;
  };
  const tasksByDay = withPlanned(taskOwner);
  // Unfinished tasks whose day has passed are shown at the top of today, not lost in the past.
  const overdueFor = (owner: string | null) => overdueTasks(allTasks, owner);
  const overdue = overdueFor(taskOwner);
  const tasksOn = (d: Date, list: Record<string, any[]>, late: any[]) =>
    dayRelation(d, new Date()) === 'today' ? [...late, ...(list[dayKey(d)] || [])] : (list[dayKey(d)] || []);
  const nameOf = (id: string) => members.find((m: any) => m.id === id)?.name || '';
  const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const weekdayName = (d: Date) => d.toLocaleDateString(locale, { weekday: 'long' });

  const dayTitle = (d: Date) => {
    const rel = dayRelation(d, new Date());
    if (rel === 'today') return i18n.t('calendar.today_day').replace('{{day}}', weekdayName(d));
    if (rel === 'tomorrow') return i18n.t('calendar.tomorrow_day').replace('{{day}}', weekdayName(d));
    return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const openCreate = () => {
    resetForm();
    const { start, end } = defaultNewEventTimes(selectedDate, new Date());
    setStartAt(start);
    setEndAt(end);
    setShowEventModal(true);
  };

  const whenText = (ev: EventDto) =>
    ev.all_day ? i18n.t('calendar.all_day_label') : i18n.t('calendar.at_time').replace('{{time}}', timeOf(ev.start_at));

  // The whole card opens the event full screen (Edit / Delete live there). Compact = a narrow
  // week column or family column: picture on top, title (max two lines) and time below.
  const eventCard = (ev: EventDto, compact = false) => {
    const picture = ev.header_image ? resolveAssetUrl(ev.header_image) : null;
    const others = (ev.attendees || []).filter((id) => id !== current_user?.id).map(nameOf).filter(Boolean);
    return (
      <li key={ev.id}>
        <button
          type="button"
          onClick={() => setViewingId(ev.id)}
          className={`w-full text-left rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark ${
            compact ? 'flex flex-col gap-2 p-2' : 'flex items-center gap-3 p-4'
          }`}
        >
          {compact ? (
            <span className="block w-full h-16">
              <PictureTile src={picture} label={ev.title} size="fill" tint="green" rounded="rounded-kid-tile" />
            </span>
          ) : (
            <PictureTile src={picture} label={ev.title} size={64} tint="green" />
          )}
          <span className="min-w-0 flex-1 block">
            <span className={`block font-bold leading-tight break-words hyphens-auto ${compact ? 'text-sm line-clamp-2' : 'text-lg'}`}>{ev.title}</span>
            <span className={`block ${compact ? 'text-xs' : 'text-sm md:text-base'}`}>
              {whenText(ev)}
              {!compact && others.length > 0 && `, ${i18n.t('calendar.with_names').replace('{{names}}', others.join(', '))}`}
            </span>
          </span>
          {!compact && <span aria-hidden className="text-xl">›</span>}
        </button>
      </li>
    );
  };

  // The note on a task: overdue / due today / tomorrow (+ time when one was set).
  const dueNote = (t: any): { text: string; tone: string } | null => {
    const info = taskDueInfo(t.due_date);
    if (!info) return null;
    const timed = (key: string) => i18n.t(info.time ? `calendar.${key}_at` : `calendar.${key}`).replace('{{time}}', info.time || '');
    if (info.kind === 'overdue') {
      const since = new Date(t.due_date).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'short' });
      return { text: i18n.t('calendar.overdue_since').replace('{{day}}', since), tone: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' };
    }
    if (info.kind === 'today') return { text: timed('due_today'), tone: 'bg-kid-peach text-kid-text dark:bg-amber-900/50 dark:text-amber-100' };
    if (info.kind === 'tomorrow') return { text: timed('due_tomorrow'), tone: 'bg-kid-chip text-kid-text dark:bg-kid-chip-dark dark:text-kid-text-dark' };
    return { text: timed('due_later'), tone: 'bg-kid-chip text-kid-text dark:bg-kid-chip-dark dark:text-kid-text-dark' };
  };

  // A task in the calendar: dashed border so it doesn't look like an event; opens the task itself.
  const taskRow = (t: any, compact = false) => {
    // A planned repeat: not a task yet, so faint and not clickable.
    const note = t.planned
      ? { text: i18n.t('calendar.planned'), tone: 'bg-kid-chip text-kid-text dark:bg-kid-chip-dark dark:text-kid-text-dark' }
      : dueNote(t);
    return (
      <li key={`task-${t.id}`}>
        <button
          type="button"
          disabled={!!t.planned}
          aria-label={t.planned ? `${t.title} — ${i18n.t('calendar.planned')}` : undefined}
          onClick={() => !t.planned && navigate('/tasks', { state: { openTaskId: t.id } })}
          className={`w-full text-left rounded-kid-card border-2 border-dashed border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark ${t.planned ? 'opacity-50 cursor-default ' : ''}${
            compact ? 'flex flex-col gap-2 p-2' : 'flex items-center gap-3 p-4'
          }`}
        >
          {compact ? (
            <span className="block w-full h-16">
              <StoredPicture url={t.header_image_url} imageId={t.header_image} label={t.title} size="fill" rounded="rounded-kid-tile" fit="cover" />
            </span>
          ) : (
            <StoredPicture url={t.header_image_url} imageId={t.header_image} label={t.title} size={64} fit="cover" />
          )}
          <span className="min-w-0 flex-1 block">
            {note && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 mb-1 font-bold ${compact ? 'text-[11px] leading-tight' : 'text-sm'} ${note.tone}`}>
                <span aria-hidden>{t.planned ? '🗓' : '✓'}</span>{note.text}
              </span>
            )}
            <span className={`block font-bold leading-tight break-words hyphens-auto ${compact ? 'text-sm line-clamp-2' : 'text-lg'}`}>{t.title}</span>
            <span className={`block ${compact ? 'text-xs' : 'text-sm md:text-base'}`}>
              {compact ? starsText(Number(t.points) || 0) : <>{i18n.t('calendar.task_label')} · {starsText(Number(t.points) || 0)}</>}
              {!compact && !isChild && t.assigned_to && ` · ${nameOf(t.assigned_to)}`}
            </span>
          </span>
          {!compact && !t.planned && <span aria-hidden className="text-xl">›</span>}
        </button>
      </li>
    );
  };

  const daySection = (d: Date, opts: { hideIfEmpty?: boolean; compact?: boolean } = {}) => {
    const key = dayKey(d);
    const evs = eventsByDay[key] || [];
    const tks = tasksOn(d, tasksByDay, overdue);
    if (opts.hideIfEmpty && evs.length === 0 && tks.length === 0) return null;
    return (
      <section key={key} className="space-y-2">
        {opts.compact ? <h3 className="sr-only">{dayTitle(d)}</h3> : <KidSectionHeading>{dayTitle(d)}</KidSectionHeading>}
        {evs.length === 0 && tks.length === 0 && opts.compact ? (
          <p className="text-center text-xl font-bold text-kid-text-muted dark:text-kid-text-muted-dark py-2" aria-label={i18n.t('calendar.nothing_planned')}>–</p>
        ) : evs.length === 0 && tks.length === 0 ? (
          <KidCard tone="mint" className="flex items-center gap-3 !py-3">
            <AntMascot pose="wave" className="w-12 h-12 shrink-0" />
            <p className="font-bold">{i18n.t('calendar.nothing_planned')}</p>
          </KidCard>
        ) : (
          <ul className="space-y-2">
            {tks.map((t) => taskRow(t, opts.compact))}
            {evs.map((ev) => eventCard(ev, opts.compact))}
          </ul>
        )}
      </section>
    );
  };

  // Week strip: Mon–Sun around the selected day; tapping a day opens it.
  const weekStrip = () => (
    <div className="grid grid-cols-7 gap-1.5 md:gap-3 mb-5">
      {daysFor('week', selectedDate).map((d) => {
        const selected = view === 'day' && d.toDateString() === selectedDate.toDateString();
        const today = dayRelation(d, new Date()) === 'today';
        return (
          <button
            key={dayKey(d)}
            type="button"
            onClick={() => { setSelectedDate(d); setView('day'); }}
            aria-pressed={selected}
            className={`min-h-[64px] md:min-h-[76px] rounded-kid-tile border-2 flex flex-col items-center justify-center font-bold ${
              selected
                ? 'bg-kid-green border-kid-green text-white'
                : 'bg-kid-card dark:bg-kid-card-dark border-kid-border dark:border-kid-border-dark'
            } ${today && !selected ? '!border-kid-green' : ''}`}
          >
            <span className="text-sm md:text-base">{d.toLocaleDateString(locale, { weekday: 'short' })}</span>
            <span className="font-kid-heading font-extrabold text-xl md:text-2xl leading-none">{d.getDate()}</span>
          </button>
        );
      })}
    </div>
  );

  const pill = (active: boolean) =>
    `min-h-[44px] px-4 rounded-full font-bold text-base whitespace-nowrap border-2 ${
      active
        ? 'bg-kid-green-bg dark:bg-green-900/40 border-kid-green text-kid-green-shadow dark:text-green-200'
        : 'bg-kid-card dark:bg-kid-card-dark border-kid-border dark:border-kid-border-dark'
    }`;

  const periodNav = (compact = false) => (
    <div className={compact ? 'mb-2' : 'mb-4'}>
      <div className="flex items-center justify-between gap-2">
        <button aria-label={i18n.t(`calendar.prev_${view}`)} title={i18n.t(`calendar.prev_${view}`)} onClick={() => shiftCurrentPeriod(-1)}
          className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold shrink-0">‹</button>
        <h2 className={`font-kid-heading font-extrabold text-center ${compact ? 'text-base' : 'text-xl md:text-2xl'}`}>{columnHeaderTitle}</h2>
        <button aria-label={i18n.t(`calendar.next_${view}`)} title={i18n.t(`calendar.next_${view}`)} onClick={() => shiftCurrentPeriod(1)}
          className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold shrink-0">›</button>
      </div>
      {!periodContains(view, selectedDate, new Date()) && (
        <div className="flex justify-center mt-1">
          <button onClick={() => setSelectedDate(new Date())} className="min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300">
            {i18n.t('calendar.go_to_today')}
          </button>
        </div>
      )}
    </div>
  );

  const viewingEvent = viewingId ? events.find((ev) => ev.id === viewingId) || null : null;
  const monthDays = daysFor('month', selectedDate);
  const monthHasItems = monthDays.some((d) => (eventsByDay[dayKey(d)] || []).length + tasksOn(d, tasksByDay, overdue).length > 0);

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl flex items-center gap-2">
          <KidIcon name="calendar" size={32} className="shrink-0" />
          {i18n.t('calendar.title')}
        </h1>
        <KidButton size="md" variant="secondary" onClick={openCreate} icon={<span aria-hidden>+</span>}>
          {i18n.t('calendar.add_event')}
        </KidButton>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)} className={pill(view === v)} aria-pressed={view === v}>
            {i18n.t(`calendar.view_${v}`)}
          </button>
        ))}
      </div>

      {/* Grown-ups: whose calendar, and the family overview */}
      {!isChild && (
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
          <button onClick={() => setMemberFilter('all')} className={pill(memberFilter === 'all')}>{i18n.t('calendar.everyone')}</button>
          {members.map((m: any) => (
            <button key={m.id} onClick={() => setMemberFilter(m.id)} className={pill(memberFilter === m.id)}>{m.name}</button>
          ))}
          {isAdmin && (
            <button onClick={() => setFamilyViewEnabled((prev) => !prev)} className={pill(familyViewEnabled)}>
              {familyViewEnabled ? i18n.t('calendar.single_view_button') : i18n.t('calendar.family_view_button')}
            </button>
          )}
        </div>
      )}

      {error && <p role="alert" className="mb-3 font-bold text-red-700 dark:text-red-300">{error}</p>}

      {!familyViewEnabled ? (
        <div>
          {periodNav()}
          {view === 'day' && weekStrip()}
          {view === 'week' && <div className="lg:hidden">{weekStrip()}</div>}
          {loading && events.length === 0 && <p className="text-base">{i18n.t('common.loading')}</p>}

          {view === 'day' && daySection(selectedDate)}

          {view === 'week' && (
            <>
              {/* phone / portrait: a list of days; ≥1024 px: seven columns */}
              <div className="space-y-5 lg:hidden">{daysFor('week', selectedDate).map((d) => daySection(d))}</div>
              {/* mock-up tablet-landscape-09: a tinted lane per day with its own header, today in green */}
              <div className="hidden lg:grid grid-cols-7 gap-3 items-stretch">
                {daysFor('week', selectedDate).map((d) => {
                  const today = dayRelation(d, new Date()) === 'today';
                  return (
                    <div
                      key={dayKey(d)}
                      className={`min-w-0 min-h-[420px] rounded-kid-card p-2 flex flex-col gap-2 ${
                        today ? 'bg-kid-green-bg dark:bg-green-900/30 border-2 border-kid-green' : 'bg-kid-peach/60 dark:bg-amber-900/20 border-2 border-transparent'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => { setSelectedDate(d); setView('day'); }}
                        className={`rounded-kid-tile py-2 text-center font-bold ${today ? 'bg-kid-green text-white' : ''}`}
                      >
                        <span className="block text-sm">{today ? i18n.t('calendar.go_to_today') : d.toLocaleDateString(locale, { weekday: 'short' })}</span>
                        <span className="block font-kid-heading font-extrabold text-xl leading-none">{d.getDate()}</span>
                      </button>
                      {daySection(d, { compact: true })}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {view === 'month' && (
            <div className="space-y-5">
              {monthHasItems ? monthDays.map((d) => daySection(d, { hideIfEmpty: true })) : (
                <KidCard tone="mint" className="flex items-center gap-3">
                  <AntMascot pose="wave" className="w-14 h-14 shrink-0" />
                  <p className="font-bold text-lg">{i18n.t('calendar.nothing_this_month')}</p>
                </KidCard>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Family view (grown-ups): one column per member */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...members].sort((a: any, b: any) => {
            const rankA = a.member_type === 'child' ? 0 : 1;
            const rankB = b.member_type === 'child' ? 0 : 1;
            if (rankA !== rankB) return rankA - rankB;
            return (a.name || '').localeCompare(b.name || '');
          }).map((m: any) => {
            const memberEvents = events.filter((ev) => Array.isArray(ev.attendees) && ev.attendees.includes(m.id));
            const byDay = groupEventsByStartDay(memberEvents);
            const memberTasks = withPlanned(m.id);
            const memberLate = overdueFor(m.id);
            const count = (d: Date) => (byDay[dayKey(d)] || []).length + tasksOn(d, memberTasks, memberLate).length;
            const days = daysFor(view, selectedDate).filter((d) => view === 'day' || count(d) > 0);
            return (
              <KidCard key={m.id} className="!p-4">
                <div className="flex items-center gap-3 mb-2">
                  <img src={getMemberAvatarSrc(m)} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-kid-border" />
                  <p className="font-kid-heading font-extrabold text-lg">{m.name}</p>
                </div>
                {periodNav(true)}
                {days.length === 0 || days.every((d) => count(d) === 0) ? (
                  <p className="text-sm">{i18n.t('calendar.nothing_planned')}</p>
                ) : (
                  <div className="space-y-3">
                    {days.map((d) => (
                      <div key={dayKey(d)}>
                        {view !== 'day' && <p className="font-bold text-sm mb-1">{d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}</p>}
                        <ul className="space-y-2">
                          {tasksOn(d, memberTasks, memberLate).map((t) => taskRow(t, true))}
                          {(byDay[dayKey(d)] || []).map((ev) => eventCard(ev, true))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </KidCard>
            );
          })}
        </div>
      )}

      {confirmDialog}
      {viewingEvent && (
        <div role="dialog" aria-modal="true" aria-label={viewingEvent.title}
          className="fixed inset-0 z-[10040] bg-kid-page dark:bg-kid-bg-dark text-kid-text dark:text-kid-text-dark overflow-y-auto">
          <div className="w-full max-w-2xl mx-auto px-4 md:px-8 py-4 md:py-7 space-y-5">
            <button
              type="button"
              onClick={() => setViewingId(null)}
              aria-label={i18n.t('calendar.back')}
              className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold flex items-center justify-center"
            >
              <span aria-hidden>←</span>
            </button>
            <div className="w-full h-56 md:h-80">
              <PictureTile src={viewingEvent.header_image ? resolveAssetUrl(viewingEvent.header_image) : null} label={viewingEvent.title} size="fill" tint="green" rounded="rounded-kid-card" />
            </div>
            <h2 className="font-kid-heading font-extrabold text-3xl md:text-4xl leading-tight break-words hyphens-auto">{viewingEvent.title}</h2>
            <div className="space-y-1 text-lg">
              <p className="font-bold">{dayTitle(new Date(viewingEvent.start_at))}</p>
              <p>
                {viewingEvent.all_day
                  ? i18n.t('calendar.all_day_label')
                  : `${timeOf(viewingEvent.start_at)} – ${timeOf(viewingEvent.end_at)}`}
              </p>
            </div>
            {viewingEvent.location && <p className="text-lg">📍 {viewingEvent.location}</p>}
            {viewingEvent.description && <p className="text-lg whitespace-pre-line break-words">{viewingEvent.description}</p>}
            {(viewingEvent.attendees || []).length > 0 && (
              <div>
                <p className="font-bold text-base mb-2">{i18n.t('calendar.event_form_attendees')}</p>
                <div className="flex flex-wrap gap-2">
                  {(viewingEvent.attendees || []).map(nameOf).filter(Boolean).map((n) => (
                    <span key={n} className="min-h-[40px] px-4 inline-flex items-center rounded-full bg-kid-chip dark:bg-kid-chip-dark font-bold">{n}</span>
                  ))}
                </div>
              </div>
            )}
            {canModifyEvent(viewingEvent) ? (
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <KidButton size="lg" className="flex-1" onClick={() => handleEdit(viewingEvent)}>{i18n.t('calendar.edit_event')}</KidButton>
                <KidButton size="lg" variant="danger-quiet" className="flex-1" onClick={() => handleDelete(viewingEvent.id)}>{i18n.t('calendar.event_form_delete')}</KidButton>
              </div>
            ) : (
              <p className="text-base">{i18n.t('calendar.not_allowed_edit_event')}</p>
            )}
          </div>
        </div>
      )}

      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10050] p-4">
          <div role="dialog" aria-modal="true" className="bg-kid-page dark:bg-kid-bg-dark text-kid-text dark:text-kid-text-dark rounded-kid-card max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-kid-border dark:border-kid-border-dark">
            <div className="sticky top-0 bg-kid-page dark:bg-kid-bg-dark border-b-2 border-kid-border dark:border-kid-border-dark p-5 flex justify-between items-center">
              <h3 className="font-kid-heading font-extrabold text-2xl">{editingId ? `${i18n.t('calendar.event_modal_title_edit_prefix') || 'Edit'}: ${title}` : (i18n.t('calendar.event_modal_title_create') || 'Create calendar event')}</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreateOrUpdate} className="space-y-4">
                <div>
                  <label className="block text-base font-bold mb-1">{i18n.t('calendar.event_form_title')}</label>
                  <input value={title} onChange={e => setTitle(e.target.value)} className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" />
                </div>
                <div>
                  <label className="block text-base font-bold mb-1">{i18n.t('calendar.event_form_image') || 'Event image (optional)'}</label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      setImageFile(file);
                      if (file) setRemoveImage(false);
                    }}
                    className="block w-full text-sm text-gray-700 dark:text-gray-300"
                  />
                  {editingId && !imageFile && !removeImage && (events.find(ev => ev.id === editingId)?.header_image) ? (
                    <div className="mt-2 flex items-center gap-3">
                      <img
                        src={resolveAssetUrl(events.find(ev => ev.id === editingId)!.header_image)}
                        alt=""
                        className="w-16 h-16 rounded-md object-cover"
                      />
                      <button type="button" onClick={() => setRemoveImage(true)} className="min-h-[44px] px-4 rounded-full border-2 border-kid-border-strong font-bold text-sm">
                        {i18n.t('calendar.remove_image') || 'Remove image'}
                      </button>
                    </div>
                  ) : null}
                  {imageFile ? (
                    <div className="mt-2 flex items-center gap-3">
                      <img src={URL.createObjectURL(imageFile)} alt="" className="w-16 h-16 rounded-md object-cover" />
                      <button type="button" onClick={() => setImageFile(null)} className="min-h-[44px] px-4 rounded-full border-2 border-kid-border-strong font-bold text-sm">
                        {i18n.t('calendar.remove_image') || 'Remove image'}
                      </button>
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className="block text-base font-bold mb-1">{i18n.t('calendar.event_form_start')}</label>
                  <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" />
                </div>
                <div>
                  <label className="block text-base font-bold mb-1">{i18n.t('calendar.event_form_end')}</label>
                  <input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" />
                </div>
                <div className="flex items-center gap-2">
                  <input id="all_day" type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} className="h-6 w-6 accent-kid-green" />
                  <label htmlFor="all_day" className="text-base font-bold">{i18n.t('calendar.event_form_all_day')}</label>
                </div>
                {current_user?.is_admin && (
                <div>
                  <label className="block text-base font-bold mb-2">{i18n.t('calendar.event_form_attendees')}</label>
                  <div className="max-h-48 overflow-auto rounded-kid-tile border-2 border-kid-border dark:border-kid-border-dark p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-kid-card dark:bg-kid-card-dark">
                    {current_family?.members?.map((m: any) => (
                      <label key={m.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selectedAttendees.includes(m.id)}
                          onChange={(e) => {
                            setSelectedAttendees(prev => e.target.checked ? [...prev, m.id] : prev.filter(id => id !== m.id));
                          }}
                        />
                        <span>{m.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                )}
                <div>
                  <label className="block text-base font-bold mb-1">{i18n.t('calendar.reminder_label')}</label>
                  <select value={reminderOffset} onChange={(e) => setReminderOffset(Number(e.target.value))} className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green">
                    <option value={0}>{i18n.t('calendar.reminder_none')}</option>
                    <option value={10}>{i18n.t('calendar.reminder_10m')}</option>
                    <option value={30}>{i18n.t('calendar.reminder_30m')}</option>
                    <option value={60}>{i18n.t('calendar.reminder_1h')}</option>
                    <option value={720}>{i18n.t('calendar.reminder_12h')}</option>
                    <option value={1440}>{i18n.t('calendar.reminder_1d')}</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-3">
                  <KidButton size="md" variant="secondary" className="flex-1" onClick={() => { setShowEventModal(false); resetForm(); }}>
                    {i18n.t('calendar.event_form_cancel')}
                  </KidButton>
                  <KidButton type="submit" size="md" className="flex-[2]" disabled={loading || !title}>
                    {i18n.t('calendar.event_form_save')}
                  </KidButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;


